'use client';

import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { PostSummary } from '@/lib/mdx';

import Fuse from 'fuse.js';
import { IoCloseOutline, IoSearchOutline, IoTrashOutline } from 'react-icons/io5';
import posthog from 'posthog-js';

interface SearchDialogProps {
  initialPosts: PostSummary[];
  className?: string;
}

// Reusable text highlighter for search terms
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  const parts = text.split(
    new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  );
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span
            key={i}
            className='text-primary bg-primary/10 rounded-sm font-bold'>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function SearchDialog({ initialPosts, className }: SearchDialogProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  useEffect(() => {
    const host = window.location.hostname;
    if (
      host.includes('blog.bioinfometrics.com') ||
      host.includes('blog.localhost')
    ) {
      setIsBlogSubdomain(true);
    }
  }, []);

  const blogPath = isBlogSubdomain ? '' : '/blog';

  // Defer the search query so the input typing remains butter smooth
  const deferredQuery = useDeferredValue(searchQuery);

  // Initialize Fuse.js for powerful, typo-tolerant fuzzy searching
  const postFuse = useMemo(
    () =>
      new Fuse(initialPosts, {
        keys: [
          { name: 'meta.title', weight: 2 }, // Title is twice as important
          { name: 'meta.description', weight: 1 },
          { name: 'meta.author', weight: 0.5 },
          { name: 'content', weight: 0.2 } // Deep search inside content
        ],
        threshold: 0.4, // Allows for some slight typos
        ignoreLocation: true
      }),
    [initialPosts]
  );

  // Load recent searches from localStorage
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('bioinfometrics-recent-searches');
      if (saved) {
        try {
          setRecentSearches(JSON.parse(saved));
        } catch {}
      }
    }
  }, [isOpen]);

  const addRecentSearch = (query: string) => {
    const cleanQuery = query.trim();
    if (!cleanQuery) return;
    const filtered = recentSearches.filter((q) => q !== cleanQuery);
    const updated = [cleanQuery, ...filtered].slice(0, 3);
    setRecentSearches(updated);
    localStorage.setItem('bioinfometrics-recent-searches', JSON.stringify(updated));
  };

  const clearRecentSearches = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches([]);
    localStorage.removeItem('bioinfometrics-recent-searches');
  };

  const filteredPosts = useMemo(() => {
    if (!deferredQuery) return initialPosts.slice(0, 5); // Show latest 5 if no query
    return postFuse.search(deferredQuery).map((result) => result.item);
  }, [deferredQuery, initialPosts, postFuse]);

  // Flatten results for keyboard navigation
  const allResults = useMemo(() => {
    return filteredPosts.map((p) => ({ type: 'post' as const, ...p }));
  }, [filteredPosts]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [deferredQuery]);

  // Handle Cmd+K global shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Handle Scroll to selected item
  useEffect(() => {
    if (isOpen) {
      const el = document.getElementById(`search-item-${selectedIndex}`);
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, isOpen]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (allResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % allResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + allResults.length) % allResults.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = allResults[selectedIndex];
      if (selected) {
        setIsOpen(false);
        addRecentSearch(searchQuery || selected.meta.title);
        posthog.capture('article_search_result_opened', {
          article_slug: selected.slug,
          result_position: selectedIndex + 1,
          had_query: Boolean(searchQuery)
        });
        router.push(`${blogPath}/${selected.slug}`);
        setSearchQuery('');
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          type='button'
          className={`border-border/40 bg-card/50 hover:bg-card hover:border-border/80 group flex w-full items-center gap-2 rounded-full border px-4 py-2 shadow-sm transition-all duration-300 md:w-64 ${className || ''}`}
          aria-label='Search posts...'>
          <IoSearchOutline className='text-muted-foreground group-hover:text-primary transition-colors duration-300' />
          <span className='text-muted-foreground flex-1 text-left text-sm font-medium'>
            Search posts...
          </span>
          <kbd className='bg-muted text-muted-foreground hidden h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100 md:inline-flex'>
            <span className='text-xs'>⌘</span>K
          </kbd>
        </button>
      </DialogTrigger>

      <DialogContent className='!sm:max-w-[700px] border-border/30 bg-card/95 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 dark:border-border/10 dark:bg-card/95 max-h-[85vh]! w-full overflow-hidden rounded-2xl! border p-0 shadow-2xl! backdrop-blur-2xl duration-300 [&>button:last-child]:hidden'>
        <VisuallyHidden>
          <DialogTitle>Search posts</DialogTitle>
        </VisuallyHidden>

        <div className='border-border/20 relative z-10 flex items-center border-b px-4 py-4'>
          <IoSearchOutline className='text-primary/80 mr-3 shrink-0 text-2xl' />
          <input
            type='text'
            placeholder='Search posts...'
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            className='text-foreground placeholder:text-muted-foreground/50 min-w-0 flex-1 appearance-none !border-0 bg-transparent text-lg !shadow-none !ring-0 !outline-none focus:!border-0 focus:!ring-0 focus:!outline-none focus-visible:!ring-0'
            autoFocus
          />
          <div className='ml-3 flex shrink-0 items-center gap-2'>
            {searchQuery && (
              <button
                type='button'
                onClick={clearSearch}
                className='text-muted-foreground/60 hover:bg-muted/50 hover:text-foreground flex size-7 items-center justify-center rounded-md p-1 transition-colors'
                title='Clear search'>
                <IoCloseOutline className='text-xl' />
              </button>
            )}
            <button
              type='button'
              onClick={() => setIsOpen(false)}
              className='border-border/20 text-muted-foreground/60 bg-muted/20 hover:bg-muted/50 hover:text-foreground hidden h-7 items-center justify-center rounded-md border px-2 py-1 text-[10px] font-bold tracking-wider uppercase transition-colors md:flex'
              aria-label='Close'>
              ESC
            </button>
          </div>
        </div>

        <div className='scrollbar-hide max-h-[60vh] overflow-y-auto p-3'>
          {/* Recent Searches Section */}
          {!searchQuery && recentSearches.length > 0 && (
            <div className='mb-4 border-b border-border/10 pb-4'>
              <div className='flex items-center justify-between px-3 mb-2'>
                <h3 className='text-muted-foreground/50 text-xs font-bold tracking-widest uppercase'>
                  Recent Searches
                </h3>
                <button
                  type='button'
                  onClick={clearRecentSearches}
                  className='text-destructive/70 hover:text-destructive hover:bg-destructive/10 flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold transition-all'>
                  <IoTrashOutline /> Clear
                </button>
              </div>
              <div className='flex flex-wrap gap-2 px-3'>
                {recentSearches.map((query) => (
                  <button
                    key={query}
                    type='button'
                    onClick={() => setSearchQuery(query)}
                    className='bg-muted/50 hover:bg-primary/10 hover:text-primary text-muted-foreground rounded-full px-3 py-1 text-xs font-medium transition-all'>
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}

          {filteredPosts.length === 0 ? (
            <div className='py-14 text-center'>
              <IoSearchOutline className='text-muted-foreground/30 mx-auto mb-3 size-8' />
              <p className='text-muted-foreground font-medium'>
                No results found.
              </p>
              <p className='text-muted-foreground/60 mt-1 text-xs'>
                Try a different search term.
              </p>
            </div>
          ) : (
            <section className='mt-2'>
              <h3 className='text-muted-foreground/50 mb-3 px-3 text-xs font-bold tracking-widest uppercase'>
                {!searchQuery ? 'Latest Posts' : 'Search Results'}
              </h3>
              <div className='flex flex-col gap-1'>
                {filteredPosts.map((post, index) => {
                  const isSelected = selectedIndex === index;
                  return (
                    <div
                      key={`post-${post.slug}`}
                      id={`search-item-${index}`}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => {
                        setIsOpen(false);
                        addRecentSearch(searchQuery || post.meta.title);
                        posthog.capture('article_search_result_opened', {
                          article_slug: post.slug,
                          result_position: index + 1,
                          had_query: Boolean(searchQuery)
                        });
                        router.push(`${blogPath}/${post.slug}`);
                        setSearchQuery('');
                      }}
                      className={`group flex cursor-pointer items-start space-x-4 rounded-xl px-3 py-3 transition-all duration-200 ${
                        isSelected
                          ? 'bg-primary/10 shadow-sm'
                          : 'hover:bg-primary/5'
                      }`}>
                      {post.meta.image ? (
                        <div
                          className={`bg-muted relative flex size-14 shrink-0 overflow-hidden rounded-lg shadow-sm ring-1 transition-all ${
                            isSelected
                              ? 'ring-primary/40'
                              : 'ring-border/20'
                          }`}>
                          <Image
                            src={post.meta.image.src}
                            alt={post.meta.image.alt || post.meta.title}
                            fill
                            className={`object-cover transition-transform duration-500 ${
                              isSelected
                                ? 'scale-110'
                                : 'group-hover:scale-110'
                            }`}
                          />
                        </div>
                      ) : (
                        <div
                          className={`text-primary flex size-14 shrink-0 items-center justify-center rounded-lg ring-1 transition-all ${
                            isSelected
                              ? 'bg-primary ring-primary/40 text-white'
                              : 'bg-primary/10 ring-primary/20'
                          }`}>
                          <IoSearchOutline className='text-lg' />
                        </div>
                      )}
                      <div className='flex flex-1 flex-col overflow-hidden pt-0.5'>
                        <div className='flex items-center justify-between'>
                          <p
                            className={`truncate text-sm font-semibold transition-colors ${
                              isSelected
                                ? 'text-primary'
                                : 'text-foreground/90'
                            }`}>
                            <HighlightText
                              text={post.meta.title}
                              query={deferredQuery}
                            />
                          </p>
                          {/* Tags/Categories Chips */}
                          <div className='hidden sm:flex gap-1.5 shrink-0 ml-2'>
                            {(post.meta.tags || post.meta.categories || [])
                              .slice(0, 2)
                              .map((tag) => (
                                <span
                                  key={tag}
                                  className='bg-muted/60 text-muted-foreground rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider'>
                                  {tag}
                                </span>
                              ))}
                          </div>
                        </div>
                        {post.meta.description && (
                          <p className='text-muted-foreground/80 mt-1 line-clamp-1 text-xs'>
                            <HighlightText
                              text={post.meta.description}
                              query={deferredQuery}
                            />
                          </p>
                        )}
                        <div className='text-muted-foreground/60 mt-1.5 flex items-center gap-3 text-[10px] font-medium tracking-wide uppercase'>
                          {post.meta.date && (
                            <span>
                              {new Date(post.meta.date).toLocaleDateString(
                                undefined,
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }
                              )}
                            </span>
                          )}
                          {post.meta.date && post.meta.readingTime && (
                            <span className='size-1 rounded-full bg-border/60' />
                          )}
                          {post.meta.readingTime && (
                            <span>{post.meta.readingTime}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Navigation Help Footer */}
        <div className='border-border/10 bg-muted/20 flex items-center justify-end border-t px-4 py-2.5 text-[10px] text-muted-foreground font-medium'>
          <div className='flex items-center gap-4'>
            <span>
              <kbd className='bg-muted text-muted-foreground rounded border px-1 py-0.5 mr-1 font-sans'>
                ↑
              </kbd>
              <kbd className='bg-muted text-muted-foreground rounded border px-1 py-0.5 font-sans'>
                ↓
              </kbd>{' '}
              Navigate
            </span>
            <span>
              <kbd className='bg-muted text-muted-foreground rounded border px-1 py-0.5 mr-1 font-sans'>
                ↵
              </kbd>{' '}
              Open
            </span>
            <span>
              <kbd className='bg-muted text-muted-foreground rounded border px-1.5 py-0.5 mr-1 font-sans'>
                esc
              </kbd>{' '}
              Close
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
